import { useEffect, useState } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Users, ClipboardList, CalendarCheck, Dumbbell, Layout, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import DirectorSummary from "../components/dashboard/DirectorSummary";

function Stat({ icon: Icon, value, label, color, to }) {
  return (
    <Link
      to={to}
      className="block group"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Card className="p-4 sm:p-6 border-slate-200 transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-md">
        <div className="flex items-start justify-between">
          <div
            className="h-9 w-9 sm:h-11 sm:w-11 rounded-xl flex items-center justify-center"
            style={{ background: `${color}15`, color }}
          >
            <Icon size={18} className="sm:hidden" />
            <Icon size={20} className="hidden sm:block" />
          </div>
        </div>
        <div className="mt-3 sm:mt-6">
          <div className="font-heading font-bold text-2xl sm:text-3xl">{value}</div>
          <div className="label-eyebrow mt-1 text-[10px] sm:text-xs">{label}</div>
        </div>
      </Card>
    </Link>
  );
}

export default function DashboardPage() {
  const { activeTeam, teams, season } = useTeams();
  const [counts, setCounts] = useState({
    players: 0,
    convocations: 0,
    trainings: 0,
    attendance: 0,
    formations: 0,
  });

  useEffect(() => {
    if (!activeTeam) return;
    (async () => {
      const [p, c, t, a, f] = await Promise.all([
        api.get(`/teams/${activeTeam.id}/players`),
        api.get(`/teams/${activeTeam.id}/convocations`, { params: { season } }),
        api.get(`/teams/${activeTeam.id}/trainings`, { params: { season } }),
        api.get(`/teams/${activeTeam.id}/attendance`, { params: { season } }),
        api.get(`/teams/${activeTeam.id}/formations`),
      ]);
      setCounts({
        players: p.data.length,
        convocations: c.data.length,
        trainings: t.data.length,
        attendance: a.data.length,
        formations: f.data.length,
      });
    })();
  }, [activeTeam, season]);

  if (!teams.length) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center">
        <div className="inline-flex h-16 w-16 rounded-2xl bg-sky-100 text-sky-600 items-center justify-center mb-6">
          <Trophy size={28} />
        </div>
        <h1 className="font-heading text-3xl font-bold mb-2">Benvenuto, Mister!</h1>
        <p className="text-slate-600 mb-6">
          Crea la tua prima squadra dal menu in alto a sinistra per iniziare.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="dashboard">
      <div>
        <div className="label-eyebrow">Panoramica</div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-1">
          {activeTeam?.name}
        </h1>
        <p className="text-sm sm:text-base text-slate-500 mt-1">
          {activeTeam?.category} · {activeTeam?.season}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        <Stat icon={Users} value={counts.players} label="Giocatori" color="#0ea5e9" to="/app/anagrafica" />
        <Stat icon={ClipboardList} value={counts.convocations} label="Convocazioni" color="#22c55e" to="/app/convocazioni" />
        <Stat icon={CalendarCheck} value={counts.attendance} label="Presenze" color="#f59e0b" to="/app/presenze" />
        <Stat icon={Dumbbell} value={counts.trainings} label="Allenamenti" color="#8b5cf6" to="/app/allenamenti" />
        <Stat icon={Layout} value={counts.formations} label="Schemi" color="#ef4444" to="/app/lavagna" />
      </div>

      {/* Director-style monthly summary */}
      <DirectorSummary teamId={activeTeam?.id} />

      <Card className="p-8 border-slate-200">
        <div className="label-eyebrow mb-2">Suggerimenti rapidi</div>
        <h2 className="font-heading text-2xl font-semibold mb-4">Da dove inizio?</h2>
        <ol className="space-y-3 text-slate-700">
          <li className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-sky-500 text-white text-sm flex items-center justify-center shrink-0 font-bold">
              1
            </span>
            Aggiungi i giocatori dall'<Link to="/app/anagrafica" className="text-sky-600 underline">anagrafica</Link> con foto e contatti dei genitori.
          </li>
          <li className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-sky-500 text-white text-sm flex items-center justify-center shrink-0 font-bold">
              2
            </span>
            Crea la prima <Link to="/app/convocazioni" className="text-sky-600 underline">convocazione</Link> e stampala in PDF.
          </li>
          <li className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-sky-500 text-white text-sm flex items-center justify-center shrink-0 font-bold">
              3
            </span>
            Pianifica un <Link to="/app/allenamenti" className="text-sky-600 underline">allenamento</Link> con esercizi.
          </li>
          <li className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-sky-500 text-white text-sm flex items-center justify-center shrink-0 font-bold">
              4
            </span>
            Disegna lo schema sulla <Link to="/app/lavagna" className="text-sky-600 underline">lavagna tattica</Link>.
          </li>
        </ol>
      </Card>
    </div>
  );
}
